import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';

/** GET/PUT `/structure/branches/{branchId}/shipping-methods`. */
export interface BranchShippingMethods {
  branchId: string;
  shippingMethodIds: string[];
}

export interface BranchShippingMethodsUpsertPayload {
  shippingMethodIds: string[];
}

/**
 * Formas de envío asociadas a una sucursal (Operación / Configuración de procesos).
 *
 * Endpoints:
 *   - GET `/api/structure/branches/{branchId}/shipping-methods`
 *   - PUT `/api/structure/branches/{branchId}/shipping-methods`
 *     body: `{ shippingMethodIds: string[] }`
 */
@Injectable({ providedIn: 'root' })
export class BranchShippingMethodsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/branches`;
  }

  get(branchId: string): Observable<BranchShippingMethods> {
    return this.httpClient
      .get<ApiResponse<BranchShippingMethods>>(
        `${this.apiUrl}/${branchId}/shipping-methods`,
      )
      .pipe(
        map((response) => ({
          branchId: response.data?.branchId || branchId,
          shippingMethodIds: Array.isArray(response.data?.shippingMethodIds)
            ? response.data!.shippingMethodIds
            : [],
        })),
      );
  }

  update(
    branchId: string,
    payload: BranchShippingMethodsUpsertPayload,
  ): Observable<BranchShippingMethods> {
    return this.httpClient
      .put<ApiResponse<BranchShippingMethods>>(
        `${this.apiUrl}/${branchId}/shipping-methods`,
        {
          shippingMethodIds: payload.shippingMethodIds ?? [],
        },
      )
      .pipe(
        map((response) => ({
          branchId: response.data?.branchId || branchId,
          shippingMethodIds: Array.isArray(response.data?.shippingMethodIds)
            ? response.data!.shippingMethodIds
            : (payload.shippingMethodIds ?? []),
        })),
      );
  }
}
